import { PATHS } from '@dadohr/shared/domainTypes/routes';
import { compact } from 'lodash';
import { useMemo } from 'react';
import { useRouter } from '../../../hooks/useRouter';
import { useTranslation } from '../../../i18next';
import { SideNavItem, SideNavProps } from '../../../layout/PageBody';
import { useRoutes } from '../../../routes';
import { useCurrentUser } from '../../../services/currentUser';
import { hasPermissionToAccessPage } from '../../../services/permissions';
import { useAllUserSavedViews } from '../../../services/savedView';

const VIEWS_PATH_PREFIX = PATHS.users.views.detail.replace(':viewId', '');

export const useUsersSideNav = (): SideNavProps => {
  const { t } = useTranslation();
  const { ROUTES, getPath } = useRoutes();
  const user = useCurrentUser();
  const { location } = useRouter();
  const newViewId = (location.state as { newViewId?: string } | null)?.newViewId;
  const [savedViews] = useAllUserSavedViews(
    user.org.id,
    user.id
  );

  const currentPath = getPath();
  const onUsersSection =
    currentPath.startsWith(PATHS.users.overview) ||
    currentPath.startsWith(VIEWS_PATH_PREFIX);

  return useMemo(() => {
    const savedViewChildren: SideNavItem[] | undefined =
      onUsersSection && savedViews?.length
        ? savedViews.map((v) => ({
            label: v.data.name,
            path: ROUTES.users.views.detail.url(v.id),
            highlight: v.id === newViewId
          }))
        : undefined;

    return {
      items: compact(
        [
          {
            label: t('pageTitle.orgUsersOverview'),
            route: ROUTES.users.overview,
            children: savedViewChildren
          },
          {
            label: t('pageTitle.orgUsersImportsOverview'),
            route: ROUTES.users.imports.overview
          },
          {
            label: t('pageTitle.orgUsersImportSources'),
            route: ROUTES.users.imports.importSources
          },
          {
            label: t('Target audiences'),
            route: ROUTES.users.targetAudiences.list
          },
          {
            label: t('pageTitle.orgUsersCustomFields'),
            route: ROUTES.org.customFields
          }
        ]
          .filter((r) => hasPermissionToAccessPage(user, r.route))
          .map((r) => ({
            label: r.label,
            path: r.route.url(),
            children: 'children' in r ? r.children : undefined
          }))
      )
    };
  }, [ROUTES, t, user, savedViews, onUsersSection, newViewId]);
};
